/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {AdnShotterService} from '../../common/adn-shotter.service';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './transparency-shotter-page.component.html';
import {SIMPLE_PAGE_IMPORTS} from './simple-page-imports';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

@Component({
  standalone: true,
  imports: SIMPLE_PAGE_IMPORTS,
  selector: 'adn-transparency-shotter-page',
  template
})
export class TransparencyShotterPageComponent implements OnInit {
  lastViewed: any[] = [];
  lastViewedObject: any;

  device = 'DESKTOP';
  private readonly asyncState = signal<any>({
    screenshots: undefined,
    screenshotting: false,
    deleting: false,
    loaded: true,
    takingScreenshot: false,
    takeSuccess: false,
    takeError: '',
    resp: null,
    missingTransparencyShots: undefined,
    fetchingMissingTransparency: false,
    transparencyByLineItemPage: undefined,
    fetchingTransparencyByLineItem: false
  });
  private readAsync(key: string): any { return this.asyncState()[key]; }
  private writeAsync(key: string, value: any): void {
    const state = this.asyncState();
    if (!Object.is(state[key], value)) {
      this.asyncState.set({...state, [key]: value});
    }
  }
  get screenshots(): any[] { return this.readAsync('screenshots'); }
  set screenshots(value: any[]) { this.writeAsync('screenshots', value); }
  get screenshotting(): boolean { return this.readAsync('screenshotting'); }
  set screenshotting(value: boolean) { this.writeAsync('screenshotting', value); }
  get deleting(): boolean { return this.readAsync('deleting'); }
  set deleting(value: boolean) { this.writeAsync('deleting', value); }
  showScreenshots = false;
  get loaded(): boolean { return this.readAsync('loaded'); }
  set loaded(value: boolean) { this.writeAsync('loaded', value); }
  get takingScreenshot(): boolean { return this.readAsync('takingScreenshot'); }
  set takingScreenshot(value: boolean) { this.writeAsync('takingScreenshot', value); }
  get takeSuccess(): boolean { return this.readAsync('takeSuccess'); }
  set takeSuccess(value: boolean) { this.writeAsync('takeSuccess', value); }
  get takeError(): string { return this.readAsync('takeError'); }
  set takeError(value: string) { this.writeAsync('takeError', value); }
  objectId: string;
  filterLineItemId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  get resp(): any { return this.readAsync('resp'); }
  set resp(value: any) { this.writeAsync('resp', value); }

  get missingTransparencyShots(): any[] { return this.readAsync('missingTransparencyShots'); }
  set missingTransparencyShots(value: any[]) { this.writeAsync('missingTransparencyShots', value); }
  get fetchingMissingTransparency(): boolean { return this.readAsync('fetchingMissingTransparency'); }
  set fetchingMissingTransparency(value: boolean) { this.writeAsync('fetchingMissingTransparency', value); }
  showMissingTransparency = false;

  transparencyByLineItemMeta = {
    currentPage: 1,
    pageSize: 20,
    resolved: true
  };
  get transparencyByLineItemPage(): any { return this.readAsync('transparencyByLineItemPage'); }
  set transparencyByLineItemPage(value: any) { this.writeAsync('transparencyByLineItemPage', value); }
  get fetchingTransparencyByLineItem(): boolean { return this.readAsync('fetchingTransparencyByLineItem'); }
  set fetchingTransparencyByLineItem(value: boolean) { this.writeAsync('fetchingTransparencyByLineItem', value); }
  showTransparencyByLineItem = false;

  constructor(
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnShotter: AdnShotterService,
    private readonly uiRouter: UIRouter,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  ngOnInit(): void {
    const lineItemId = this.uiRouter.globals.params.lineItemId;
    if (lineItemId) {
      this.objectId = lineItemId;
      this.filterLineItemId = lineItemId;
      this.doCheck();
    }
  }

  doCheck(): void {
    if (!this.objectId) {
      this.takeSuccess = false;
      this.takeError = 'Enter a line item ID first.';
      return;
    }
    this.loaded = false;
    this.takingScreenshot = true;
    this.takeSuccess = false;
    this.takeError = '';
    this.resp = null;
    this.adnShotter.takeTransparency(this.objectId, this.device).then((data: any) => {
      this.resp = data;
      this.takeSuccess = true;
    }).catch((error: any) => {
      this.takeError = _.get(error, ['data', 'message'])
        || _.get(error, ['data'])
        || 'Unable to queue the transparency screenshot.';
      if (!_.isString(this.takeError)) {
        this.takeError = 'Unable to queue the transparency screenshot.';
      }
    }).finally(() => {
      this.loaded = true;
      this.takingScreenshot = false;
    });
  }

  refresh(): void {
    this.screenshotting = true;
    this.showScreenshots = true;
    this.adnShotter.fetchAllTransparency().then((data: any[]) => {
      this.screenshots = data;
    }).finally(() => {
      this.screenshotting = false;
    });
  }

  refreshMissingTransparency(): void {
    this.fetchingMissingTransparency = true;
    this.showMissingTransparency = true;
    this.adnShotter.missingTransparency().then((data: any[]) => {
      this.missingTransparencyShots = data;
    }).finally(() => {
      this.fetchingMissingTransparency = false;
    });
  }

  refreshTransparencyByLineItem(): void {
    this.fetchTransparencyByLineItem(this.transparencyByLineItemMeta.currentPage);
  }

  changeTransparencyByLineItemPage(page: number): void {
    if (page < 1 || page > this.transparencyByLineItemTotalPages() || page === this.transparencyByLineItemMeta.currentPage) {
      return;
    }
    this.transparencyByLineItemMeta.currentPage = page;
    this.fetchTransparencyByLineItem(page);
  }

  transparencyByLineItemTotalPages(): number {
    return Math.max(1, Math.ceil((this.transparencyByLineItemPage?.totalHits || 0) / this.transparencyByLineItemMeta.pageSize));
  }

  transparencyByLineItemPageNumbers(): number[] {
    return visiblePageNumbers(this.transparencyByLineItemMeta.currentPage, this.transparencyByLineItemTotalPages());
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting = true;
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      _.pullAt(this.screenshots, index);
    }).finally(() => {
      this.deleting = false;
    });
  }

  private fetchTransparencyByLineItem(page?: number): void {
    this.fetchingTransparencyByLineItem = true;
    this.showTransparencyByLineItem = true;
    this.adnShotter.transparencyByLineItem(page || this.transparencyByLineItemMeta.currentPage, this.filterLineItemId).then((data: any) => {
      this.transparencyByLineItemMeta.currentPage = data.page;
      this.transparencyByLineItemMeta.pageSize = data.pageSize;
      this.transparencyByLineItemPage = {
          totalHits: data.totalCount,
          pageStart: data.totalCount ? ((data.page - 1) * data.pageSize) + 1 : 0,
          pageEnd: Math.min(data.totalCount, data.page * data.pageSize),
          items: _.map(data.results, (screenshots: any[], lineItemId: string) => ({
            lineItemId: lineItemId,
            lineItem: _.get(screenshots, [0, 'lineItem']),
            screenshots: screenshots
          }))
      };
    }).finally(() => {
      this.fetchingTransparencyByLineItem = false;
    });
  }
}
